import * as React from "react"
import { TextField, FontIcon, Dialog, FlatButton, SelectField, MenuItem, Toggle, Chip } from "material-ui"
import FinddDatePicker from "../../../components/common/FinddDatePicker"
import strings from "../../../localization/strings"
import autobind from "class-autobind"
import AccountStore from "../../../stores/AccountStore"
import { IEmployeeAccrual, IAccrual, IUserDocument, IAccrualAdjustment, EAccrualType } from "../../../models"
import moment from "moment"
import ConfirmDialog from "../../../components/common/ConfirmDialog"
import AddManualAdjustmentDialog from "../user-details/AddManualAdjustmentDialog"
import * as api from "../../../api"
import { blueGrey500 } from "material-ui/styles/colors"
import * as payGroupAndTypeHelper from "../../../helpers/payGroupAndTypeHelper"
import * as utils from "../../../helpers/utils"
import * as timeHelper from "../../../helpers/timeHelper"
import authenticationStore from "../../../stores/AuthenticationStore"
import * as permissionHelper from "../../../helpers/permissionHelper"

interface IProps {
  user: IUserDocument
  existingAccruals: IEmployeeAccrual[]
  editAccrual: IEmployeeAccrual
  handleClose(): void
  handleAdd(payload: IEmployeeAccrual): void
  handleUpdate(payload: IEmployeeAccrual): void
}
interface IState {
  name: string
  errorName: string
  active: boolean
  balance: string
  errorBalance: string
  allowUseForLeaveTracking: boolean
  accrualStartDate: string
  errorAccrualStartDate: string
  enableProbationDate: boolean
  accrualEndProbationDate: string
  errorAccrualEndProbationDate: string
  linkedAccountAccrualId: string
  errorLinkedAccountAccrualId: string
  confirmDialogContent: string
  confirmDialogAction: string
  confirmDialogPayload: IEmployeeAccrual
  allowEdit: boolean
  openManualAdjustmentDialog: boolean
  note: string
  adjustmentReason: IAccrualAdjustment
  editAccrual: IEmployeeAccrual
  monetaryAccrual: boolean
}
const confirmDialogActions = {
  UpdateBalance: "UpdateBalance",
  UpdateUserForLeaveTracking: "UpdateUserForLeaveTracking"
}

export default class AddUserDetailAccrual extends React.Component<IProps, IState> {
  private accrualStartDateElem
  private accrualProbationEndDateElem
  constructor(props) {
    super(props)
    const accountAccrual = this.props.editAccrual ?
      AccountStore.getCurrentAccount().Accruals.find(item => item.Id === this.props.editAccrual.LinkedAccountAccrualId) : null

    this.state = {
      name: this.props.editAccrual ? this.props.editAccrual.Name : "",
      errorName: "",
      active: this.props.editAccrual ? this.props.editAccrual.Active : true,
      balance: this.props.editAccrual ? String(utils.roundToTwo(this.props.editAccrual.Balance)) : "0",
      errorBalance: "",
      allowUseForLeaveTracking: this.props.editAccrual ? this.props.editAccrual.AllowUseForLeaveTracking : false,
      accrualStartDate: this.props.editAccrual ? this.props.editAccrual.AccrualStartDate : moment().utc(true).startOf("day").format(),
      errorAccrualStartDate: "",
      enableProbationDate: this.props.editAccrual ? this.props.editAccrual.EnableProbationDate : false,
      accrualEndProbationDate:
        this.props.editAccrual ?
          this.props.editAccrual.AllowUseForLeaveTracking ?
            this.props.editAccrual.EnableProbationDate ?
              this.props.editAccrual.AccrualEndProbationDate :
              null :
            moment(this.props.editAccrual.AccrualStartDate).utc(true).startOf("day").format() :
          null,
      errorAccrualEndProbationDate: "",
      linkedAccountAccrualId: this.props.editAccrual ? this.props.editAccrual.LinkedAccountAccrualId : null,
      errorLinkedAccountAccrualId: "",
      confirmDialogContent: "",
      confirmDialogAction: "",
      confirmDialogPayload: null,
      allowEdit: this.props.editAccrual && this.props.editAccrual.AllowUseForLeaveTracking ? false : true,
      openManualAdjustmentDialog: false,
      note: this.props.editAccrual && this.props.editAccrual.Note ? this.props.editAccrual.Note : "",
      adjustmentReason: this.props.editAccrual && this.props.editAccrual.AdjustmentReason ? this.props.editAccrual.AdjustmentReason : undefined,
      editAccrual: this.props.editAccrual,
      monetaryAccrual: accountAccrual ? accountAccrual.Type === EAccrualType.Monetary : false
    }
    autobind(this)
  }

  componentDidMount() {
    this.props.editAccrual && this.props.editAccrual.AllowUseForLeaveTracking && this.getAllowEditEmpAccrual(this.props.editAccrual.Id)
  }

  getAllowEditEmpAccrual(accrualId) {
    api.getAllowChangeEmpAccrual(this.props.user.Id, accrualId).then(res => this.setState({ allowEdit: res.data }))
  }

  handleSave() {
    if (!this.checkValidate()) return
    if (this.state.editAccrual) {
      const balance = Number(this.state.balance)
      const balanceChanged = utils.roundToTwo(this.props.editAccrual.Balance) !== balance
      const payload = Object.assign({}, this.state.editAccrual)
      payload.Name = this.state.name.trim()
      payload.Active = this.state.active
      payload.Balance = balanceChanged ? balance : this.props.editAccrual.Balance
      payload.AllowUseForLeaveTracking = this.state.allowUseForLeaveTracking
      payload.AccrualStartDate = this.state.accrualStartDate
      payload.EnableProbationDate = this.state.enableProbationDate
      payload.AccrualEndProbationDate = this.state.allowUseForLeaveTracking ? this.state.accrualEndProbationDate : null
      payload.LinkedAccountAccrualId = this.state.linkedAccountAccrualId
      payload.Note = this.state.note
      payload.AdjustmentReason = this.state.adjustmentReason
      payload.IsMonetary = this.state.monetaryAccrual
      balanceChanged ?
        this.setState({
          confirmDialogContent: strings.Are_you_sure_you_want_to_change_balance,
          confirmDialogPayload: payload,
          confirmDialogAction: confirmDialogActions.UpdateBalance
        }) :
        this.props.handleUpdate(payload)
    }
    else {
      const payload: IEmployeeAccrual = {
        Id: utils.guid(),
        Name: this.state.name.trim(),
        Active: this.state.active,
        StartingBalance: Number(this.state.balance),
        Balance: Number(this.state.balance),
        PendingBalance: Number(this.state.balance),
        AllowUseForLeaveTracking: this.state.allowUseForLeaveTracking,
        LinkedAccountAccrualId: this.state.linkedAccountAccrualId,
        AccrualStartDate: this.state.accrualStartDate,
        EnableProbationDate: this.state.enableProbationDate,
        AccrualEndProbationDate: this.state.allowUseForLeaveTracking ? this.state.accrualEndProbationDate : null,
      }
      this.props.handleAdd(payload)
    }
  }

  checkValidate() {
    let isValidate = true
    if (this.state.name.trim().length === 0) {
      this.setState({ errorName: strings.NameRequired })
      isValidate = false
    }
    else if (this.state.name.trim().length > 50) {
      this.setState({ errorName: strings.formatString(strings.Name_requires_maximum_chars, 50) as string })
      isValidate = false
    }
    else if (this.props.existingAccruals.filter((accrual) => this.state.editAccrual ? accrual.Id !== this.state.editAccrual.Id : true).find((existingAccrual) => existingAccrual.Name === this.state.name.trim())) {
      this.setState({ errorName: strings.Accrual_name_is_duplicated })
      isValidate = false
    }
    else {
      this.setState({ errorName: "" })
    }

    if (!this.state.balance.trim()) {
      this.setState({ errorBalance: strings.This_field_is_required })
      isValidate = false
    }
    else if (Number.isNaN(Number(this.state.balance))) {
      this.setState({ errorBalance: strings.Invalid_format })
      isValidate = false
    }
    else if (utils.getDecimalCount(Number(this.state.balance)) > 2) {
      this.setState({ errorBalance: strings.formatString(strings.Allowed_only_x_digit_decimal_point, 2) as string })
      isValidate = false
    }
    else {
      if (this.state.linkedAccountAccrualId) {
        const linkedAccountAccrual = AccountStore.getCurrentAccount().Accruals.find(item => item.Id === this.state.linkedAccountAccrualId)
        const maxBalance = linkedAccountAccrual.MaxBalance ? linkedAccountAccrual.MaxBalance : 1000
        const minBalance = linkedAccountAccrual.MinBalance ? linkedAccountAccrual.MinBalance : 0
        if (Number(this.state.balance) > maxBalance) {
          this.setState({ errorBalance: strings.formatString(strings.This_field_must_be_less_than_or_equal_to_x, maxBalance) as string })
          isValidate = false
        }
        else if (Number(this.state.balance) < minBalance) {
          this.setState({ errorBalance: strings.formatString(strings.This_field_must_be_greater_than_or_equal_to_x, minBalance) as string })
          isValidate = false
        }
        else {
          this.setState({ errorBalance: "" })
        }
      }
      else {
        this.setState({ errorBalance: "" })
      }
    }

    if (this.state.accrualStartDate === null) {
      this.setState({ errorAccrualStartDate: strings.This_field_is_required })
      isValidate = false
    }
    else {
      this.setState({ errorAccrualStartDate: "" })
    }
    if (this.state.allowUseForLeaveTracking) {
      if (this.state.enableProbationDate) {
        if (this.state.accrualEndProbationDate === null) {
          this.setState({ errorAccrualEndProbationDate: strings.This_field_is_required })
          isValidate = false
        }
        else if (moment(timeHelper.keepConvertUTCToLocal(this.state.accrualStartDate)).startOf("day").isAfter(moment(timeHelper.keepConvertUTCToLocal(this.state.accrualEndProbationDate)).startOf("day"))) {
          this.setState({ errorAccrualEndProbationDate: strings.Invalid_date_range })
          isValidate = false
        }
        else {
          this.setState({ errorAccrualEndProbationDate: "" })
        }
      }
      else {
        this.setState({ errorAccrualEndProbationDate: "" })
      }
    }
    if (!this.state.linkedAccountAccrualId) {
      this.setState({ errorLinkedAccountAccrualId: strings.This_field_is_required })
      isValidate = false
    }
    else {
      const linkedPayTypes = payGroupAndTypeHelper.getLinkedPayTypes(
        payGroupAndTypeHelper.getPayGroupIdByPayrollPayGroupData({
          PayrollHierarchyItemId: this.props.user.PayrollHierarchyItemId,
          PayrollPayGroupId: this.props.user.PayrollPayGroupId
        })
      )
      const accountAccrual = AccountStore.getCurrentAccount().Accruals.find(item => item.Id === this.state.linkedAccountAccrualId)
      if (accountAccrual.ContributingPayTypeIds.find(id => !linkedPayTypes.find(pt => pt.Id === id))) {
        this.setState({ errorLinkedAccountAccrualId: strings.Its_not_valid_for_pay_group })
        isValidate = false
      }
      else if (accountAccrual.AllowUseForLeaveTracking && !linkedPayTypes.find(pt => pt.Id === accountAccrual.PayTypeIdUsedForLeavePunchSet)) {
        this.setState({ errorLinkedAccountAccrualId: strings.Its_not_valid_for_pay_group })
        isValidate = false
      }
      else {
        this.setState({ errorLinkedAccountAccrualId: "" })
      }
    }
    return isValidate
  }

  handleConfirm(action, payload) {
    this.setState({
      confirmDialogContent: "",
      confirmDialogPayload: null,
      confirmDialogAction: ""
    })
    switch (action) {
      case confirmDialogActions.UpdateBalance:
        this.props.handleUpdate(payload)
        break
      case confirmDialogActions.UpdateUserForLeaveTracking:
        this.setState({ allowUseForLeaveTracking: payload })
        break
    }
  }

  render() {
    return (
      <Dialog
        open
        title={this.state.editAccrual ? strings.Edit_Accrual_to_Employee : strings.Add_Accrual_to_Employee}
        titleStyle={{ paddingLeft: 35, paddingBottom: 10 }}
        actions={[
          <FlatButton
            data-intercom-target={"menu-Employees-Accruals-Btn-CloseAddAccrualDialog"}
            label={strings.CANCEL}
            secondary
            onClick={this.props.handleClose}
          />,
          <FlatButton
            data-intercom-target={"menu-Employees-Accruals-Btn-SaveAccrual"}
            label={strings.SAVE}
            primary
            onClick={this.handleSave}
          />
        ]}
      >
        <hr />
        <div style={{ paddingLeft: 15, paddingRight: 15 }}>
          {this.props.editAccrual &&
            <Toggle
              data-intercom-target={"menu-Employees-Accruals-Toggle-Active"}
              toggled={this.state.active}
              label={strings.Active}
              onToggle={(e, value) => this.setState({ active: value })}
              style={{ width: 125, marginTop: 25 }}
            />
          }
          <TextField
            data-intercom-target={"menu-Employees-Accruals-Entry-Name"}
            floatingLabelText={strings.Name}
            value={this.state.name}
            style={{ width: "95%" }}
            onChange={(e, value) => { this.setState({ name: value }, () => this.checkValidate()) }}
            errorText={this.state.errorName}
            errorStyle={{ position: "absolute", bottom: -12 }}
            disabled={permissionHelper.hasPermission(authenticationStore.Permissions.Accrual.EditAccrual) ? false: !this.state.allowEdit}
          />
          <div style={{ display: "flex" }}>
            <div style={{ float: "left", width: "45%", position: "relative" }}>
              {AccountStore.getAccrualManualAdjustmentEnabled() && this.state.editAccrual ?
                <div>
                  <div style={{ fontSize: 12, marginTop: 10 }}>{strings.Balance}</div>
                  <Chip
                    data-intercom-target={"menu-Employees-Accruals-Chip-OpenAccrualAdjustmentDialog"}
                    style={{ width: "95%", cursor: "pointer", marginTop: 8 }}
                    onTouchTap={() => {
                      if (this.state.errorBalance !== "") this.setState({ balance: String(utils.roundToTwo(this.state.editAccrual.Balance)), errorBalance: "" })
                      this.setState({ openManualAdjustmentDialog: true })
                    }}
                  >
                    {`${this.state.balance} ${(this.state.monetaryAccrual ? "Dollars" : strings.Hours)}`}
                  </Chip>
                  <span style={{ position: "absolute", color: "red", fontSize: 12 }}>{this.state.errorBalance}</span>
                </div> :
                <div>
                  <TextField
                    data-intercom-target={"menu-Employees-Accruals-Entry-Balance"}
                    floatingLabelText={strings.Balance}
                    value={this.state.balance}
                    style={{ width: "95%" }}
                    onChange={(e, value) => { this.setState({ balance: value }, () => this.checkValidate()) }}
                    errorText={this.state.errorBalance}
                    errorStyle={{ position: "absolute", bottom: -12 }}
                  />
                  <div style={{ position: "absolute", right: 18, top: 40 }}>{(this.state.monetaryAccrual ? "Dollars" : strings.Hours)}</div>
                </div>
              }
            </div>
            {!this.state.monetaryAccrual &&
              <div style={{ float: "left", marginLeft: 35, marginTop: 25 }}>
                <Toggle
                  data-intercom-target={"menu-Employees-Accruals-Toggle-AllowUserForLeaveTracking"}
                  toggled={this.state.allowUseForLeaveTracking}
                  label={strings.Use_for_Leave_Tracking}
                  onToggle={(e, value) => {
                    if (!this.state.allowEdit) {
                      this.setState({
                        confirmDialogAction: confirmDialogActions.UpdateUserForLeaveTracking,
                        confirmDialogPayload: value,
                        confirmDialogContent: strings.Are_you_sure_you_want_to_change_this_value_as_Global_Admin
                      })
                    }
                    else {
                      const accAccrual = AccountStore.getCurrentAccount().Accruals.filter(item => value ? item.AllowUseForLeaveTracking : true).find(accrual => accrual.Id === this.state.linkedAccountAccrualId)
                      this.setState({
                        allowUseForLeaveTracking: value,
                        linkedAccountAccrualId: accAccrual ? this.state.linkedAccountAccrualId : null,
                        enableProbationDate: false
                      }, () => this.checkValidate())
                    }
                  }}
                  disabled={!this.state.allowEdit}
                />
              </div>
            }
          </div>
          <div>
            {this.state.allowUseForLeaveTracking &&
              <Toggle
                data-intercom-target={"menu-Employees-Accruals-Toggle-Enable_Probation_Date"}
                toggled={this.state.enableProbationDate}
                label={strings.Enable_Probation_Date}
                onToggle={(e, value) =>
                  this.setState({
                    enableProbationDate: value,
                    accrualEndProbationDate: value ? this.state.accrualEndProbationDate : null
                  }, () =>
                    this.checkValidate()
                  )
                }
                style={{ width: 205, marginTop: 15, marginLeft: 345 }}
                disabled={!this.state.allowEdit}
              />
            }
          </div>
          <div style={{ display: "flex" }}>
            <div style={{ float: "left", width: "45%", position: "relative" }}>
              <FinddDatePicker
                data-intercom-target={"menu-Employees-Accruals-Select-StartDate"}
                ref={(elem) => this.accrualStartDateElem = elem}
                floatingLabelText={strings.Accrual_Start_Date}
                formatDate={(date) => moment(date).format("MM/DD/YYYY")}
                onChange={(e, date) => this.setState({ accrualStartDate: moment(date).utc(true).startOf("day").format() }, () => this.checkValidate())}
                value={this.state.accrualStartDate && new Date(moment(this.state.accrualStartDate).local().subtract(moment(this.state.accrualStartDate).utcOffset(), "minutes").format())}
                errorText={this.state.errorAccrualStartDate}
                errorStyle={{ position: "absolute", top: 70 }}
                disabled={!this.state.allowEdit}
                cancelLabel={<span style={{ color: blueGrey500 }}>{strings.Cancel}</span>}
                firstDayOfWeek={0}
              />
              <FontIcon
                data-intercom-target={"menu-Employees-Accruals-Btn-OpenStartDatePicker"}
                className="fa fa-calendar"
                style={{ position: "absolute", top: 35, right: 60, fontSize: 21, color: "rgba(97, 97, 97, 0.6)", cursor: this.state.allowEdit ? "pointer" : "not-allowed" }}
                onClick={() => this.state.allowEdit && this.accrualStartDateElem.DatePicker.openDialog()}
              />
            </div>
            {(this.state.allowUseForLeaveTracking || this.state.monetaryAccrual) &&
              <div style={{ float: "left", marginLeft: 35, position: "relative" }}>
                <FinddDatePicker
                  data-intercom-target={"menu-Employees-Accruals-Select-EndDate"}
                  ref={(elem) => this.accrualProbationEndDateElem = elem}
                  floatingLabelText={strings.Accrual_Probation_End_Date}
                  formatDate={(date) => moment(date).format("MM/DD/YYYY")}
                  onChange={(e, date) => this.setState({ accrualEndProbationDate: moment(date).utc(true).startOf("day").format() }, () => this.checkValidate())}
                  value={this.state.accrualEndProbationDate &&
                    new Date(moment(this.state.accrualEndProbationDate).local().subtract(moment(this.state.accrualEndProbationDate).utcOffset(), "minutes").format())}
                  errorText={this.state.errorAccrualEndProbationDate}
                  errorStyle={{ position: "absolute", top: 70 }}
                  disabled={!this.state.allowEdit || !this.state.enableProbationDate}
                  cancelLabel={<span style={{ color: blueGrey500 }}>{strings.Cancel}</span>}
                  firstDayOfWeek={0}
                  floatingLabelStyle={{ top: "none", bottom: 8 }}
                />
                <FontIcon
                  data-intercom-target={"menu-Employees-Accruals-Btn-OpenEndDatePicker"}
                  className="fa fa-calendar"
                  style={{ position: "absolute", top: 35, right: 5, fontSize: 21, color: "rgba(97, 97, 97, 0.6)", cursor: this.state.allowEdit ? "pointer" : "not-allowed" }}
                  onClick={() => this.state.allowEdit && this.state.enableProbationDate &&
                    this.accrualProbationEndDateElem.DatePicker.openDialog()
                  }
                />
              </div>
            }
          </div>
          <div style={{ display: "flex" }}>
            <SelectField
              data-intercom-target={"menu-Employees-Accruals-Select-AccrualType"}
              floatingLabelText={strings.Accrual_Type}
              value={this.state.linkedAccountAccrualId}
              onChange={(e, key, payload) => {
                const accountAccrual = AccountStore.getCurrentAccount().Accruals.find(item => item.Id === payload)
                const isMonetary = (accountAccrual ? accountAccrual.Type === EAccrualType.Monetary : false)
                this.setState({ linkedAccountAccrualId: payload, monetaryAccrual: isMonetary }, () => this.checkValidate())
              }}
              errorText={this.state.errorLinkedAccountAccrualId}
              disabled={!this.state.allowEdit}
            >
              {AccountStore.getCurrentAccount().Accruals
                .filter(item => item.AllowUseForLeaveTracking === (this.state.allowUseForLeaveTracking ? item.AllowUseForLeaveTracking : false))
                .map((accrual: IAccrual, key) =>
                  <MenuItem key={key} value={accrual.Id} primaryText={accrual.Name} />
                )}
            </SelectField>
          </div>
        </div>
        {this.state.confirmDialogContent &&
          <ConfirmDialog
            open={true}
            confirmContent={this.state.confirmDialogContent}
            action={this.state.confirmDialogAction}
            close={() => this.setState({
              confirmDialogContent: "",
              confirmDialogPayload: null,
              confirmDialogAction: "",
              balance: this.state.confirmDialogAction === confirmDialogActions.UpdateBalance ? String(utils.roundToTwo(this.state.editAccrual.Balance)) : this.state.balance
            })}
            confirm={this.handleConfirm}
            payload={this.state.confirmDialogPayload}
          />
        }
        {this.state.openManualAdjustmentDialog &&
          <AddManualAdjustmentDialog
            adjustments={AccountStore.getCurrentAccountSettings().AccrualSettings && AccountStore.getCurrentAccountSettings().AccrualSettings.Adjustments}
            isNoteRequired={AccountStore.getCurrentAccountSettings().AccrualSettings && AccountStore.getCurrentAccountSettings().AccrualSettings.RequireAdjustmentNote}
            isAdjustmentReasonRequired={AccountStore.getCurrentAccountSettings().AccrualSettings && AccountStore.getCurrentAccountSettings().AccrualSettings.RequireAdjustmentReason}
            balance={this.state.balance}
            updateBalance={updatedValue => this.setState({ balance: String(utils.roundToTwo(Number(updatedValue))) }, () => this.checkValidate())}
            setNote={value => this.setState({ note: value })}
            setAdjustmentReason={value => this.setState({ adjustmentReason: value })}
            handleClose={() => this.setState({ openManualAdjustmentDialog: false })}
          />
        }
      </Dialog>
    )
  }
}
